// Keeping the resident's place when the rail itself changes shape (#206, #211).
//
// Its own module rather than more of `./anchor.ts` or of `mountAxis`: both are
// at the line limits ADR-0015 sets, and „a file that needs more room needs a
// split". Nothing about the behaviour changed in the move.
import { viewState } from '../state/viewstate.ts';
import { type Anchor, anchorOf, reserveTail, scrollForAnchor, setScroll } from './anchor.ts';

/**
 * Keeps the resident's place across a re-wrap of the rows (#206, #211).
 *
 * Lifted out of `mountAxis` unchanged: that function is at the line limit
 * ADR-0015 sets and this was always anchoring rather than rendering.
 */
export function holdThroughResize(rail: HTMLElement, list: HTMLElement, kreska: HTMLElement): void {
  // What the resident was looking at, taken while it still means something
  // (#206).
  //
  // A resize is not a rebuild, and it reports itself only AFTER the rows have
  // re-wrapped. By then the rail's own geometry can no longer say where the
  // resident was: `anchorOf` would read the NEW boundary against the untouched
  // scroll and hand back exactly the state that has to be undone. So the anchor
  // is kept up to date as they scroll, and the observer below only puts it back.
  //
  // A scroll of the resident's that lands in the same frame as the resize is
  // the one thing this cannot answer. A `scroll` event is dispatched in a
  // frame's rendering steps, which run before that frame's observer callbacks,
  // so such a scroll re-takes the anchor from the new layout and putting it
  // back changes nothing: that resident keeps their place and loses the
  // re-anchor, which is what the rail did before this existed. Measured ten
  // times on the built app — the seat 700 px into the past and the window
  // narrowed in the same breath, same answer with and without a guard against
  // it. WHICH frame an event lands in is not something this can lean on: the
  // review watched a resize's own clamp arrive in the update AFTER the
  // observer, which is equally allowed and equally harmless here, because an
  // anchor taken then describes a rail nobody has moved since.
  //
  // Two walks of the rows per scroll event, and it is not a cost: measured on
  // the built app under mobile emulation, 0,024 ms on a folded day of 69 rows
  // and 0,030 ms with „starsze" unfolded at 81 — against a 16 ms frame.
  let held: Anchor | null = null;
  const capture = () => {
    // Never from a rail with no height. A phone turned on its side leaves the
    // rail `clientHeight` 0 (#211), and a rail of no height shows no rows: the
    // visibility filter in `holdsOf` rejects every one of them, so the anchor
    // falls back to its last resort, the bare distance to the band. That
    // distance is not invariant under the re-wrap that produced it — the
    // restore INTO landscape honoured a ROW, and the gap that fell out of it is
    // a different promise from the one the resident had. Turning back spends it
    // and the row being read comes back 81 px low (review 1, which measured 56
    // px on a live day). Keeping the upright anchor brings them back to the row.
    if (rail.clientHeight === 0) return;
    held = anchorOf(rail, list, kreska, viewState.get().order === 'czas');
  };
  rail.addEventListener('scroll', capture, { passive: true });

  // The paper under the last row is measured from the rail's own height, and
  // the rail changes height without a render: the grip resizes the circle block
  // above it (§5.3, D-h), and so does the desktop breakpoint. Left to the next
  // poll the reservation would be up to ten seconds stale, and in between the
  // band could not be brought to the rail's top on a short day. The RAIL is
  // what is observed, never the list: the reservation changes the list's own
  // height, and observing that would be a loop.
  new ResizeObserver(() => {
    reserveTail(list, kreska);
    // …and then the same rule a rebuild answers to (§5.3): pinned stays pinned,
    // otherwise the row being read keeps its own height on the screen. AFTER
    // the reservation, because that is what decides how far the rail may be
    // scrolled at all: on a quiet evening with less ahead of „teraz" than the
    // rail is tall, a window grown taller needs more paper before the band can
    // reach the edge, and restoring first leaves it 355 px down a rail the
    // browser has clamped (measured, and the scenario that holds it).
    //
    // The grip and the dial never needed this and still do not: they change the
    // rail's HEIGHT alone, and neither term of the boundary — the list's place
    // in the scroll, the rows standing above the band — moves with it, so the
    // number this computes is the one the rail already has.
    //
    // Nothing is captured here afterwards. The write fires a `scroll` of its
    // own, and where it changes nothing the anchor it would re-take is the one
    // already held — the restore preserved it. Measured on the built app with
    // and without: identical on all six states, including both round trips.
    const was = held;
    if (was) setScroll(rail, () => scrollForAnchor(rail, list, kreska, was));
  }).observe(rail);
}
